import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute } from '@tanstack/react-router';
import { Button } from 'components/Button';
import { TextField } from 'components/FormComponents';
import { Heading } from 'components/Heading';
import { ChevronLeft } from 'lucide-react';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ForgotPasswordRecordSchema, type ForgotPasswordRecord } from 'shared/modelTypes/user';

export const Route = createFileRoute('/forgot-password')({
	component: RouteComponent,
});

function RouteComponent() {
	const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

	const methods = useForm<ForgotPasswordRecord>({
		resolver: zodResolver(ForgotPasswordRecordSchema),
		// defaultValues: OptionalUserRecordSchema.parse({}),
	});

	const onSubmit = async (data: ForgotPasswordRecord) => {
		try {
			const res = await fetch(`${import.meta.env.VITE_WEBSOCKET_URL}/forgot-password?email=${data.email}`);
			if (res.ok) {
				setStatus('success');
			} else {
				setStatus('error');
			}
		} catch (err) {
			console.error('errors', err);
			setStatus('error');
		}
	};

	if (status === 'success') {
		return (
			<div className="text-center">
				<Heading>Password reset</Heading>
				<p className="max-w-sm">
					If your email address is registered, your password has been successfully reset. You will receive an email with
					instructions to reset your password. Click below to log in.
				</p>
				<Button size="md" className="mt-6 w-full" to="/login">
					Go to login
				</Button>
			</div>
		);
	}

	if (status === 'error') {
		return (
			<div className="text-center">
				<Heading>Password reset failed</Heading>
				<p className="max-w-sm">Something went wrong while sending the reset link. Please try again or check your email address.</p>
				<Button size="lg" className="mt-6 w-full" to="/forgot-password">
					Try again
				</Button>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-2 text-center">
			<Heading>Forgot password?</Heading>
			<p className="max-w-sm">No problem! If your email is registered, we'll send you a link to reset your password.</p>
			<FormProvider {...methods}>
				<form className="flex flex-col gap-3" onSubmit={methods.handleSubmit(onSubmit)}>
					<TextField name="email" label="" placeholder="Email address" />
					<Button type="submit" className="mt-md w-full">
						Reset password
					</Button>
				</form>
			</FormProvider>

			<Button mode="tertiary" className="w-full" to="/login">
				<ChevronLeft className="size-5" /> Back to login
			</Button>
		</div>
	);
}
